import React, { useState, useEffect } from 'react';
import { Palette, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import api from '../utils/api';

const PageThemePanel = ({ theme, onChange }) => {
  const [siteTheme, setSiteTheme] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchSiteTheme();
  }, []);

  const fetchSiteTheme = async () => {
    try {
      const response = await api.get('/theme');
      setSiteTheme(response.data);
    } catch (error) {
      console.error('Failed to fetch site theme');
    }
  };

  const pageTheme = theme || { use_site_theme: true };

  const updatePageTheme = (key, value) => {
    onChange({ ...pageTheme, [key]: value });
  };

  const gradientDirections = [
    { value: 'to bottom', label: 'Top to Bottom' },
    { value: 'to right', label: 'Left to Right' },
    { value: 'to bottom right', label: 'Diagonal ↘' },
    { value: 'to bottom left', label: 'Diagonal ↙' },
  ];

  // Get effective value (page override or site default)
  // Mirrors getThemeValue() logic in PreviewRenderer.js for consistency
  const getEffectiveValue = (key) => {
    if (pageTheme.use_site_theme === false && pageTheme[key]) {
      return pageTheme[key];
    }
    return siteTheme?.[key] || '';
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border border-slate-200 rounded-lg">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-4 h-auto" data-testid="page-theme-panel-toggle">
          <div className="flex items-center gap-2">
            <Palette size={18} className="text-purple-500" />
            <span className="font-medium">Page Theme</span>
          </div>
          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="px-4 pb-4">
        <div className="space-y-4">
          {/* Use Site Theme Toggle */}
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <div>
              <Label className="text-sm font-medium">Use Site Theme</Label>
              <p className="text-xs text-slate-500">Inherit settings from site-wide theme</p>
            </div>
            <Switch
              checked={pageTheme.use_site_theme !== false}
              onCheckedChange={(checked) => updatePageTheme('use_site_theme', checked)}
              data-testid="page-theme-use-site-toggle"
            />
          </div>

          {/* Page-specific overrides */}
          {pageTheme.use_site_theme === false && (
            <div className="space-y-4 pt-2">
              {/* Background Type */}
              <div>
                <Label className="text-sm">Background Type</Label>
                <Select 
                  value={pageTheme.background_type || 'solid'} 
                  onValueChange={(v) => updatePageTheme('background_type', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solid">Solid Color</SelectItem>
                    <SelectItem value="linear-gradient">Linear Gradient</SelectItem>
                    <SelectItem value="radial-gradient">Radial Gradient</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Solid Color */}
              {(!pageTheme.background_type || pageTheme.background_type === 'solid') && (
                <div>
                  <Label className="text-sm">Background Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={pageTheme.background_color || '#ffffff'}
                      onChange={(e) => updatePageTheme('background_color', e.target.value)}
                      className="w-12 h-9 p-1 cursor-pointer"
                      data-testid="page-theme-bg-color-picker"
                    />
                    <Input
                      value={pageTheme.background_color || '#ffffff'}
                      onChange={(e) => updatePageTheme('background_color', e.target.value)}
                      placeholder="#ffffff"
                      className="flex-1"
                      data-testid="page-theme-bg-color-input"
                    />
                  </div>
                </div>
              )}

              {/* Gradient Options */}
              {pageTheme.background_type && pageTheme.background_type !== 'solid' && (
                <>
                  <div>
                    <Label className="text-sm">Gradient Direction</Label>
                    <Select 
                      value={pageTheme.gradient_direction || 'to bottom'} 
                      onValueChange={(v) => updatePageTheme('gradient_direction', v)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {gradientDirections.map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Start Color</Label>
                      <div className="flex gap-1 mt-1">
                        <Input
                          type="color"
                          value={pageTheme.gradient_start || '#6366f1'}
                          onChange={(e) => updatePageTheme('gradient_start', e.target.value)}
                          className="w-10 h-9 p-1 cursor-pointer"
                        />
                        <Input
                          value={pageTheme.gradient_start || '#6366f1'}
                          onChange={(e) => updatePageTheme('gradient_start', e.target.value)}
                          className="flex-1 text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">End Color</Label>
                      <div className="flex gap-1 mt-1">
                        <Input
                          type="color"
                          value={pageTheme.gradient_end || '#8b5cf6'}
                          onChange={(e) => updatePageTheme('gradient_end', e.target.value)}
                          className="w-10 h-9 p-1 cursor-pointer"
                        />
                        <Input
                          value={pageTheme.gradient_end || '#8b5cf6'}
                          onChange={(e) => updatePageTheme('gradient_end', e.target.value)}
                          className="flex-1 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Text Colors */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Heading Color</Label>
                  <div className="flex gap-1 mt-1">
                    <Input
                      type="color"
                      value={pageTheme.heading_color || '#1e293b'}
                      onChange={(e) => updatePageTheme('heading_color', e.target.value)}
                      className="w-10 h-9 p-1 cursor-pointer"
                    />
                    <Input
                      value={pageTheme.heading_color || '#1e293b'}
                      onChange={(e) => updatePageTheme('heading_color', e.target.value)}
                      className="flex-1 text-xs"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Body Color</Label>
                  <div className="flex gap-1 mt-1">
                    <Input
                      type="color"
                      value={pageTheme.body_color || '#475569'}
                      onChange={(e) => updatePageTheme('body_color', e.target.value)}
                      className="w-10 h-9 p-1 cursor-pointer"
                    />
                    <Input
                      value={pageTheme.body_color || '#475569'}
                      onChange={(e) => updatePageTheme('body_color', e.target.value)}
                      className="flex-1 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Primary Color */}
              <div>
                <Label className="text-sm">Primary/Accent Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={pageTheme.primary_color || '#4f46e5'}
                    onChange={(e) => updatePageTheme('primary_color', e.target.value)}
                    className="w-12 h-9 p-1 cursor-pointer"
                    data-testid="page-theme-primary-color-picker"
                  />
                  <Input
                    value={pageTheme.primary_color || '#4f46e5'}
                    onChange={(e) => updatePageTheme('primary_color', e.target.value)}
                    className="flex-1"
                    data-testid="page-theme-primary-color-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Preview of current theme */}
          <div className="mt-4 p-4 rounded-lg border border-slate-200" 
            style={{
              background: pageTheme.use_site_theme !== false 
                ? (siteTheme?.background_type?.includes('gradient') 
                    ? `linear-gradient(${siteTheme?.gradient_direction || 'to bottom'}, ${siteTheme?.gradient_start || '#6366f1'}, ${siteTheme?.gradient_end || '#8b5cf6'})`
                    : siteTheme?.background_color || '#ffffff')
                : (pageTheme.background_type?.includes('gradient')
                    ? `linear-gradient(${pageTheme.gradient_direction || 'to bottom'}, ${pageTheme.gradient_start || '#6366f1'}, ${pageTheme.gradient_end || '#8b5cf6'})`
                    : pageTheme.background_color || '#ffffff')
            }}
          >
            <p className="text-xs font-medium mb-1" 
              style={{ color: getEffectiveValue('heading_color') || '#1e293b' }}>
              Preview Heading
            </p>
            <p className="text-xs" 
              style={{ color: getEffectiveValue('body_color') || '#475569' }}>
              This is how text will appear on this page.
            </p>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default PageThemePanel;
